import { isCompletedAuditTask } from '../../utils/auditPipelineUtils';

const DAY_MS = 24 * 60 * 60 * 1000;

const cleanLabel = (value, fallback = 'Not specified') => String(value || '').trim() || fallback;

const cycleLabel = (value) => {
  const normalized = cleanLabel(value).toLowerCase();
  if (normalized.includes('initial')) return 'Initial';
  if (normalized.includes('surveillance')) return 'Surveillance';
  if (normalized.includes('recert')) return 'Recertification';
  return cleanLabel(value);
};

const aggregateProjects = (tasks, selector) => {
  const projectsByLabel = new Map();
  tasks.forEach((task) => {
    const label = selector(task);
    if (!projectsByLabel.has(label)) projectsByLabel.set(label, new Set());
    projectsByLabel.get(label).add(String(task?.projectId ?? `task:${task?.id}`));
  });
  return [...projectsByLabel.entries()]
    .map(([label, projects]) => ({ label, value: projects.size }))
    .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label));
};

export const filterAuditManagementTasks = (tasks = [], filter, now = new Date()) => {
  const list = Array.isArray(tasks) ? tasks : [];
  if (!filter?.type || !filter?.value) return list;
  if (filter.type === 'standard') return list.filter((task) => cleanLabel(task?.standardName) === filter.value);
  if (filter.type === 'program') return list.filter((task) => cleanLabel(task?.serviceName) === filter.value);
  if (filter.type === 'cycle') return list.filter((task) => cycleLabel(task?.auditCycle) === filter.value);
  if (filter.type === 'process') {
    const taskCodes = new Set(Array.isArray(filter.taskCodes) ? filter.taskCodes : []);
    return list.filter((task) => taskCodes.has(String(task?.taskTypeCode || '').trim().toUpperCase()));
  }
  if (filter.type === 'risk') {
    if (filter.value === 'active') return list.filter((task) => !isCompletedAuditTask(task?.status));
    if (filter.value === 'completed') return list.filter((task) => isCompletedAuditTask(task?.status));
    if (filter.value === 'unassigned') {
      return list.filter((task) => !isCompletedAuditTask(task?.status) && !task?.assignedUsername);
    }
    if (filter.value === 'assigned') {
      return list.filter((task) => !isCompletedAuditTask(task?.status) && task?.assignedUsername);
    }
    if (filter.value === 'stale') {
      const staleThreshold = now.getTime() - (14 * DAY_MS);
      return list.filter((task) => {
        const timestamp = Date.parse(task?.updatedAt || task?.createdAt || '');
        return !isCompletedAuditTask(task?.status) && Number.isFinite(timestamp) && timestamp < staleThreshold;
      });
    }
  }
  return list;
};

export const buildAuditManagementAnalytics = (tasks = [], now = new Date()) => {
  const list = Array.isArray(tasks) ? tasks : [];
  const active = list.filter((task) => !isCompletedAuditTask(task?.status));
  const staleThreshold = now.getTime() - (14 * DAY_MS);

  return {
    byStandard: aggregateProjects(list, (task) => cleanLabel(task?.standardName)),
    byService: aggregateProjects(list, (task) => cleanLabel(task?.serviceName)),
    byCycle: aggregateProjects(list, (task) => cycleLabel(task?.auditCycle)),
    signals: {
      activeTasks: active.length,
      completedTasks: list.length - active.length,
      unassignedTasks: active.filter((task) => !task?.assignedUsername).length,
      staleTasks: active.filter((task) => {
        const timestamp = Date.parse(task?.updatedAt || task?.createdAt || '');
        return Number.isFinite(timestamp) && timestamp < staleThreshold;
      }).length,
      ownerCount: new Set(active.map((task) => task?.assignedUsername).filter(Boolean)).size,
    },
  };
};
